import React, { Dispatch, useState, useMemo } from 'react';
import { Autocomplete, Stack, TextField, Button, FormControl, FormLabel } from '@mui/material';
import StyledModal from '../../Components/GenericModal/StyledModal';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Api from '../API';
import { ApplicationAction, IApplication } from '../types';

import { sharedClasses } from '../../Components/CustomUIElements/sharedClasses';
import ModalFooterButtons from '../../Components/GenericModal/ModalFooterButtons';
import { styles } from './styles';
import { classes } from '../../Components/GenericModal/styles';

export default function ExportCsvModal({ dispatch }: { dispatch: Dispatch<ApplicationAction> }) {
  const queryClient = useQueryClient();
  const application = queryClient.getQueryData<IApplication>(['application']);

  const [selectedTemplate, setSelectedTemplate] = useState<{ value: number; label: string } | null>(
    null
  );
  const [candidateData, setCandidateData] = useState<{ label: string; value: string; column: string }[]>([]);
  const [error, setError] = useState<string>('');

  const { data: templates, isLoading: loadingTemplates } = useQuery({
    queryKey: ['csv export templates'],
    queryFn: async () => {
      const { res } = await Api.getCsvExportTemplates();
      const templates = res as { csv_export_templates: Array<{ value: number; label: string }> };
      return templates.csv_export_templates;
    },
    onError: (e: { error: string }) =>
      dispatch({
        type: 'SET_SNACKBAR',
        payload: {
          message: `There was an error getting export templates: ${e.error}`,
          state: 'error'
        }
      }),
    initialData: [],
    enabled: !!application
  });

  const { isFetching: loadingCandidateData, refetch: fetchCandidateData } = useQuery({
    queryKey: ['csv export candidate data', application?.id],
    queryFn: async () => {
      if (!application || !selectedTemplate) return;
      const { res } = await Api.getCandidateDataWithCsvTemplate(
        application.job.id,
        application.id,
        selectedTemplate.value
      );
      const result = res as { csv: { rows: string[][] }; meta: Array<{ label: string; column: string }> };

      setCandidateData(
        result.meta.map((row: { label: string; column: string }, index: number) => {
          return {
            ...row,
            value: result.csv.rows[0][index]
          };
        })
      );
      return result;
    },
    onError: (e: { error: string }) =>
      dispatch({
        type: 'SET_SNACKBAR',
        payload: {
          message: `There was an error getting candidate data: ${e.error}`,
          state: 'error'
        }
      }),
    initialData: [],
    enabled: false
  });

  const handleDownload = () => {
    const headers = candidateData.map((obj) => `"${obj.column}"`).join(',');
    const row1 = candidateData.map((obj) => obj.value === null ? '' : `"${obj.value}"`).join(',');
    const csvString = [headers, row1].join('\n');

    const blob = new Blob([csvString], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'candidate_export.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    dispatch({
      type: 'SET_SNACKBAR',
      payload: { message: 'CSV file downloaded', state: 'success' }
    });
    handleClose();
  };

  const handleClose = () => dispatch({ type: 'SET_MODALS_OPEN', payload: null });

  const handleInputChange = (index: number, newValue: string) => {
    setCandidateData((prevData) =>
      prevData.map((item, i) => (i === index ? { ...item, value: newValue } : item))
    );
  };

  const CandidateDataDisplay = useMemo(() => {
    if (candidateData.length === 0) return null;
    const rows = candidateData.map((row, index) => {
      return (
        <FormControl sx={{ marginTop: '1rem' }} key={index}>
          <FormLabel required={false}>{row.label}</FormLabel>
          <TextField
            error={false}
            value={row.value}
            onChange={(e: { target: { value: string } }) =>
              handleInputChange(index, e.target.value)
            }
          />
        </FormControl>
      );
    });
    return <Stack sx={{ marginLeft: 0.5 }}>{rows}</Stack>;
  }, [candidateData]);

  return (
    <StyledModal
      isOpen={true}
      label="Export CSV modal"
      handleClose={handleClose}
      styleOverrides={styles.modalStyleOverrides}
    >
      <Stack sx={styles.modalContainer}>
        <Stack sx={styles.modalTitle}>Export to CSV</Stack>
        <Stack sx={{ paddingRight: '2px', overflow: 'auto', paddingBottom: 3 }}>
          <Autocomplete
            disablePortal
            options={templates}
            getOptionLabel={(option: { label: string }) => option.label}
            value={selectedTemplate}
            sx={{ ...sharedClasses.formAutocomplete, paddingTop: 4, flexGrow: 1 }}
            loading={loadingTemplates}
            loadingText="Loading templates..."
            ListboxProps={{ style: sharedClasses.autoCompleteListStyles }}
            renderInput={(params: object) => (
              <TextField
                {...params}
                label="Which service would you like to export to?"
                InputLabelProps={{ shrink: true }}
                required
                sx={styles.autoCompleteTextfield}
                helperText={error}
              />
            )}
            onChange={(_event, value) => {
              value ? setError('') : setError('Must select a template');
              setSelectedTemplate(value);
            }}
          />

          <Button
            sx={{ ...classes.modalButtonBase, width: 'fit-content', margin: '1rem 0' }}
            disabled={!selectedTemplate || loadingCandidateData}
            onClick={(e) => {
              e.preventDefault();
              fetchCandidateData();
            }}
            loading={loadingCandidateData}
          >
            Gather candidate data
          </Button>
          {CandidateDataDisplay}
        </Stack>
        <ModalFooterButtons
          primaryButtonText="Export CSV"
          primaryButtonCallback={handleDownload}
          secondaryButtonText="Cancel"
          secondaryButtonCallback={handleClose}
          primaryButtonDisabled={candidateData.length === 0}
        />
      </Stack>
    </StyledModal>
  );
}
